<template>
  <v-page>
    <v-card class="elevation-4">
      <v-form v-model="valid" @submit.prevent="login()" ref="form">
            <v-card-title class="pb-4">
              <h4>Welcome to the McKinsey Hub</h4>
            </v-card-title>

        <v-divider></v-divider>
        <v-card-text>
            <v-btn large color="#4285F4" class="white--text mt-2 mb-4" block @click.stop="midClick()" :disabled="submitted">Log in with Client email</v-btn>

            <div v-show="loginMode" class="mb-4">
              <v-divider></v-divider>
            </div>

            <v-btn v-show="!loginMode" large color="rgb(0, 174, 171)" class="white--text mt-2 mb-4" block @click.stop="loginMode='corporate'" :disabled="submitted">Log in with Firm email (@mckinsey.com)</v-btn>

            <v-btn v-if="isDevelopment" v-show="!loginMode" large color="success" class="white--text mt-2 mb-4" block @click.stop="loginMode='password'" :disabled="submitted">Log in with Password</v-btn>

            <div v-show="loginMode != null">

                <v-text-field prepend-icon="account_box"
                                name="email"
                                label="Email"
                                type="text"
                                :rules="emailRules"
                                v-model="email"
                                data-test="login"
                                required
                                :disabled="submitted">
                </v-text-field>

                <v-text-field prepend-icon="lock"
                                id="password"
                                name="password"
                                type="password"
                                label="Password"
                                :rules="[(v) => !!v || loginProvider != 'password' || 'Password is required']"
                                v-model="password"
                                data-test="password"
                                required
                                v-show="loginMode == 'password'"
                                :disabled="submitted">
                </v-text-field>

                <v-btn block type="submit" large color="rgb(0, 174, 171)" data-test="submitLogin" class="white--text mb-3">Log in</v-btn>

              </div>

              <v-divider></v-divider>

              <div class="my-3">

              <v-select v-if="isDevelopment || !server"
               prepend-icon="domain"
               name="server"
               label="Environment"
               :rules="[(v) => !!v || 'Environment is required']"
               v-model="server"
               :items="servers"
               :hint="serverUrl"
               item-text="name"
               item-value="key"
               persistent-hint
               data-test="environments"
               required
               :disabled="submitted"
               class="mb-3">
                 <template v-slot:item="{ item }">
                   <div class="v-list__tile__content" :data-test="`environment${item.name}`">
                     <div class="v-list__tile__title">{{ item.name }}</div>
                   </div>
                 </template>
              </v-select>
              <v-alert
                type="info"
                color="blue-grey"
                :value="true"
                v-show="!isProduction"
                >

                You will log in to <strong>{{ server }}</strong> at <em>{{ serverUrl }}</em>
              </v-alert>

          </div>

              <div v-if="submitted">
              <v-progress-linear :indeterminate="true"></v-progress-linear>
              </div>
        </v-card-text>
      </v-form>
    </v-card>
  </v-page>
</template>

<script>
import { servers, Rules } from '../helpers'
import VPage from '../layouts/Minimal.vue'
import { Bus } from '../bus'
import shajs from 'sha.js'
import CurrentSession from '../mixins/CurrentSession.vue'
import { mid } from '@/services/Mid'

export default {
  name: 'Login',
  mixins: [CurrentSession],
  components: {
    VPage
  },
  data () {
    return {
      valid: false,
      email: '',
      password: '',
      server: null,
      loginMode: null,
      loginProvider: null,
      submitted: false,
      servers: Object.keys(servers).map(key => ({ ...servers[key], key })),
      isDevelopment: process.env.NODE_ENV === 'development',
      cognitoVerifierKey: 'hub_login_verifier',
      cognitoServerKey: 'hub_login_server'
    }
  },
  computed: {
    serverUrl () {
      return this.server && this.server in servers ? servers[this.server].platform : ''
    },
    emailRules () {
      return [
        (v) => !!v || this.loginProvider === 'mckid' || 'Email is required',
        ...Rules.account.email
      ]
    }
  },
  created () {
    const urlParams = new URLSearchParams(window.location.search)
    this.server = Object.keys(servers).find(server => document.location.href.includes(servers[server].hub))
    this.loginMode = urlParams.get('loginMode')
    this.loginProvider = urlParams.get('loginProvider')
    const cognitoCode = urlParams.get('cognitoCode')
    const authCode = urlParams.get('mckidCode')

    if (cognitoCode) {
      return this.resumeCognitoLogin(cognitoCode)
    }
    if (authCode) {
      this.sendLoginRequest(this.server, {
        code: authCode,
        redirect_uri: servers[this.server].mckid['redirectUrl'],
        code_verifier: window.sessionStorage.getItem(servers[this.server].mckid['verifier_key'])
      })
    }

    if (this.loginProvider) {
      this.login()
    }
  },
  methods: {
    midClick () {
      this.loginProvider = 'mckid'
      this.loginMckId()
    },
    login () {
      if (this.loginMode === 'corporate') {
        this.loginProvider = this.email.split('@')[1].toLowerCase()
        this.loginCognito(this.loginProvider)
      } else if (this.loginMode === 'password' && this.password) {
        this.sendLoginRequest(this.server, { email: this.email, password: this.password })
      }
    },
    async loginMckId () {
      this.submitted = true
      await mid.login()
    },
    loginCognito (provider) {
      this.submitted = true

      const cognito = servers[this.server].cognito
      const codeChallenge = this.setupCognitoVerifierAndChallenge()

      window.location = cognito.url +
        '?client_id=' + cognito.clientId +
        '&response_type=code' +
        '&redirect_uri=' + this.getCognitoRedirectUri() +
        '&identity_provider=' + provider +
        '&code_challenge_method=S256' +
        '&code_challenge=' + codeChallenge
    },
    sendLoginRequest (server, payload) {
      this.submitted = true
      this.$store.dispatch('auth/login', { server, payload })
        .then(() => {
          this.$router.push({ name: 'profile' })
          if (servers[this.server].auth_cookie) {
            this.$cookies.set('IMB_API_TOKEN', this.$store.getters['auth/token'], '1d', null, servers[this.server].auth_cookie, window.location.href.protocol === 'https', 'Lax')
          }
        })
        .catch(({ message }) => {
          Bus.$emit('alert', { color: 'error', message })
          this.submitted = false
        })
    },
    setupCognitoVerifierAndChallenge () {
      // Create verifier key and store with environment in localStorage
      const bytes = new Int8Array(32)
      const codeVerifier = crypto.getRandomValues(bytes).toString()
      localStorage.setItem(this.cognitoCodeVerifierKey, codeVerifier)
      localStorage.setItem(this.cognitoServerKey, this.server)

      const hashed = shajs('sha256').update(codeVerifier).digest()
      const codeChallenge = this.base64URLEncode(hashed)

      return codeChallenge
    },
    resumeCognitoLogin (cognitoCode) {
      // Remove the code from the browser url
      history.pushState(null, '', location.href.split('?')[0])

      // Login with Cognito
      const server = localStorage.getItem(this.cognitoServerKey)
      if (!server) {
        Bus.$emit('alert', { color: 'error', message: 'Storage error, please ensure you are not running in Incognito or Private mode' })
        return
      }
      const cognito = servers[server].cognito
      this.sendLoginRequest(server, {
        cognito_client_id: cognito.clientId,
        cognito_code: cognitoCode,
        cognito_redirect_uri: this.getCognitoRedirectUri(),
        cognito_code_verifier: localStorage.getItem(this.cognitoCodeVerifierKey)
      })
    },
    getCognitoRedirectUri () {
      return (window.location.protocol + '//' + window.location.hostname + (window.location.port ? (':' + window.location.port) : '') + window.location.pathname).replace('login', '')
    },
    base64URLEncode (buffer) {
      return buffer.toString('base64')
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '')
    }
  }
}
</script>
